'use client';

import React, {
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
} from 'react';
import { cn } from '../../../lib/utils';
import { SearchIcon, CloseIcon } from '../../../icons';

// ============================================
// SearchModal Context
// ============================================
type SearchModalContextValue = {
  triggerSearch: () => void;
};

const SearchModalContext = createContext<SearchModalContextValue | null>(null);

export const useSearchModal = () => {
  const context = useContext(SearchModalContext);
  if (!context) {
    // 컨텍스트가 없으면 no-op 반환 (SearchModal 밖에서 사용해도 에러 안남)
    return { triggerSearch: () => {} };
  }
  return context;
};

// ============================================
// useIsMobile Hook (Internal)
// ============================================
const useIsMobile = (breakpoint: number = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < breakpoint);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
};

// ============================================
// SearchModal.Trigger
// ============================================
type SearchModalTriggerProps = {
  value?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  responsive?: boolean;
  onClick?: () => void;
};

const SearchModalTrigger: React.FC<SearchModalTriggerProps> = ({
  value = '',
  placeholder = '검색어를 입력하세요',
  className,
  disabled = false,
  responsive = true,
  onClick,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group relative flex items-center rounded-md cursor-pointer',
        responsive ? 'h-8 px-3 md:h-9 md:px-4' : 'h-9 px-4',
        'bg-white border border-gscale-200',
        'hover:bg-gscale-50 transition-colors duration-200',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      <SearchIcon className="flex-shrink-0 text-gscale-500 w-4 h-4" />
      <span
        className={cn(
          'flex-1 ml-2 text-xs text-left',
          value ? 'text-gscale-800' : 'text-gscale-500',
        )}
      >
        {value || placeholder}
      </span>
    </button>
  );
};

SearchModalTrigger.displayName = 'SearchModal.Trigger';

// ============================================
// SearchModal.Section
// ============================================
type SearchModalSectionProps = {
  title?: string;
  children: React.ReactNode;
  className?: string;
};

const SearchModalSection: React.FC<SearchModalSectionProps> = ({
  title,
  children,
  className,
}) => {
  return (
    <div className={cn('py-3 md:py-4', className)}>
      {title && (
        <h4 className="px-4 md:px-5 mb-2 md:mb-3 text-xs font-semibold text-gscale-500 uppercase tracking-wider">
          {title}
        </h4>
      )}
      <div>{children}</div>
    </div>
  );
};

SearchModalSection.displayName = 'SearchModal.Section';

// ============================================
// SearchModal.FilterChip
// ============================================
type SearchModalFilterChipProps = {
  label: string;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
};

const SearchModalFilterChip: React.FC<SearchModalFilterChipProps> = ({
  label,
  selected = false,
  onClick,
  className,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium',
        'transition-colors duration-200',
        selected
          ? 'bg-gscale-900 text-white'
          : 'bg-gscale-100 text-gscale-700 hover:bg-gscale-200',
        className,
      )}
    >
      {label}
    </button>
  );
};

SearchModalFilterChip.displayName = 'SearchModal.FilterChip';

// ============================================
// SearchModal.Checkbox
// ============================================
type SearchModalCheckboxProps = {
  label: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  className?: string;
};

const SearchModalCheckbox: React.FC<SearchModalCheckboxProps> = ({
  label,
  checked = false,
  onChange,
  className,
}) => {
  return (
    <label
      className={cn(
        'flex items-center gap-2 md:gap-3 px-4 md:px-5 py-2 md:py-2.5 cursor-pointer',
        'hover:bg-gscale-50 transition-colors duration-200',
        className,
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange?.(e.target.checked)}
        className="w-4 h-4 md:w-5 md:h-5 rounded border-gscale-300 text-gscale-900 focus:ring-gscale-500"
      />
      <span className="text-sm md:text-base text-gscale-800">{label}</span>
    </label>
  );
};

SearchModalCheckbox.displayName = 'SearchModal.Checkbox';

// ============================================
// SearchModal.Input
// ============================================
type SearchModalInputProps = {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
};

const SearchModalInput: React.FC<SearchModalInputProps> = ({
  label,
  value = '',
  onChange,
  placeholder = '',
  className,
}) => {
  return (
    <div className={cn('px-4 md:px-5 py-2 md:py-2.5', className)}>
      {label && (
        <label className="block mb-1.5 text-xs font-medium text-gscale-600">
          {label}
        </label>
      )}
      <input
        type="text"
        value={value}
        onChange={e => onChange?.(e.target.value)}
        placeholder={placeholder}
        className={cn(
          'w-full px-3 py-2 text-sm',
          'bg-white border border-gscale-200 rounded-md',
          'text-gscale-800 placeholder:text-gscale-500',
          'focus:outline-none focus:border-gscale-400',
          'transition-colors duration-200',
        )}
      />
    </div>
  );
};

SearchModalInput.displayName = 'SearchModal.Input';

// ============================================
// SearchModal (Main Component)
// ============================================
type SearchModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  children?: React.ReactNode;
  maxWidth?: string;
  maxHeight?: string;
  className?: string;
};

const SearchModalRoot: React.FC<SearchModalProps> = ({
  open,
  onOpenChange,
  value = '',
  onChange,
  onSearch,
  placeholder = '검색어를 입력하세요',
  children,
  maxWidth = '500px',
  maxHeight = '80vh',
  className,
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  const currentValue = onChange ? value : internalValue;

  // 모달 열릴 때 input focus
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // ESC 키로 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (onChange) {
      onChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(currentValue);
      onOpenChange(false);
    }
  };

  const handleSearch = () => {
    onSearch?.(currentValue);
    onOpenChange(false);
  };

  const handleClear = () => {
    if (onChange) {
      onChange('');
    } else {
      setInternalValue('');
    }
    inputRef.current?.focus();
  };

  const handleClose = () => onOpenChange(false);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-gscale-950/50 flex items-start justify-center z-[99999] p-4 md:p-6 pt-[10vh] md:pt-[15vh]"
      onClick={handleClose}
    >
      <div
        className={cn(
          'w-full bg-white rounded-xl md:rounded-2xl shadow-2xl',
          'border border-gscale-200',
          'flex flex-col overflow-hidden',
          'animate-in fade-in-0 slide-in-from-top-4 duration-200',
          className,
        )}
        style={{ maxWidth, maxHeight }}
        onClick={e => e.stopPropagation()}
      >
        {/* Search Input Header */}
        <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 border-b border-gscale-100">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 md:px-4 md:py-2.5 bg-gscale-50 rounded-md">
            <SearchIcon className="flex-shrink-0 text-gscale-500 w-4 h-4 md:w-5 md:h-5" />
            <input
              ref={inputRef}
              type="text"
              value={currentValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={cn(
                'flex-1 text-xs text-gscale-800',
                'bg-transparent outline-none',
                'placeholder:text-gscale-500',
              )}
            />
            {currentValue && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 rounded-full text-gscale-500 hover:text-gscale-700 hover:bg-gscale-200 transition-colors"
              >
                <CloseIcon className="w-3 h-3 md:w-4 md:h-4" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleSearch}
            className={cn(
              'px-3 py-1.5 md:px-4 md:py-2 rounded-md',
              'text-xs font-medium',
              'bg-gscale-900 text-white',
              'hover:bg-gscale-800 transition-colors duration-200',
            )}
          >
            검색
          </button>
        </div>

        {/* Modal Content */}
        <SearchModalContext.Provider value={{ triggerSearch: handleSearch }}>
          <div className="flex-1 overflow-y-auto">{children}</div>
        </SearchModalContext.Provider>

        {/* Footer with keyboard hints (desktop only) */}
        {!isMobile && (
          <div className="flex items-center justify-center gap-4 px-4 py-2.5 border-t border-gscale-100 bg-gscale-50">
            <div className="flex items-center gap-1.5 text-xs text-gscale-500">
              <kbd className="px-1.5 py-0.5 bg-white border border-gscale-200 rounded text-gscale-600 font-mono">
                Enter
              </kbd>
              <span>검색</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gscale-500">
              <kbd className="px-1.5 py-0.5 bg-white border border-gscale-200 rounded text-gscale-600 font-mono">
                ESC
              </kbd>
              <span>닫기</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SearchModalRoot.displayName = 'SearchModal';

// ============================================
// Compound Component Export
// ============================================
export const SearchModal = Object.assign(SearchModalRoot, {
  Trigger: SearchModalTrigger,
  Section: SearchModalSection,
  FilterChip: SearchModalFilterChip,
  Checkbox: SearchModalCheckbox,
  Input: SearchModalInput,
});

export type {
  SearchModalProps,
  SearchModalTriggerProps,
  SearchModalSectionProps,
  SearchModalFilterChipProps,
  SearchModalCheckboxProps,
  SearchModalInputProps,
};
